/**
 * MineManager — Manages mine lifecycle, proximity detection, and chain reactions.
 *
 * - Mines are spawned by robots with MINE_LAYER components (via passiveMineLayerTick)
 * - Each frame, checks enemy proximity against idle mines → triggers countdown
 * - Handles chain reaction queue (pushback into another mine → delayed detonation)
 * - Clears all mines on WAVE_COMPLETED
 */

import Phaser from 'phaser';
import { TILE_SIZE } from '@/config/GameConfig';
import { MINE_CONFIG } from '@/components/ComponentDefs';
import { Mine, MineState, MineStats } from '@/entities/Mine';
import { Enemy } from '@/entities/Enemy';
import { SpatialHash } from '@/systems/SpatialHash';
import { EventBus, GameEvents } from '@/events/EventBus';
import { SoundManager } from '@/systems/SoundManager';

interface ChainEntry {
  mine: Mine;
  delay: number; // seconds remaining before detonation
  depth: number;
}

export class MineManager {
  private scene: Phaser.Scene;
  private mines: Mine[] = [];
  private mineHash = new SpatialHash<Mine>(TILE_SIZE);

  /** Track how many active mines per tile: "col_row" → count */
  private tileMineCounts = new Map<string, number>();

  /** Queued chain-reaction detonations */
  private chainQueue: ChainEntry[] = [];

  /** Enemy spatial hash reference — set each frame from update() */
  private currentEnemies: Enemy[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;

    // Clear mines when wave ends
    EventBus.on(GameEvents.WAVE_COMPLETED, this.clearAll, this);
  }

  // ── Spawning ──

  /**
   * Spawn a mine on a tile. Returns null if tile is full.
   * Position is randomized within the tile to avoid overlap.
   */
  spawn(
    robotX: number, robotY: number,
    col: number, row: number,
    stats: MineStats, evolutionLevel: number,
    textureKey: string,
  ): Mine | null {
    const maxPerTile = MINE_CONFIG.maxMinesPerTile + evolutionLevel;
    const tileKey = `${col}_${row}`;
    const currentCount = this.tileMineCounts.get(tileKey) ?? 0;
    if (currentCount >= maxPerTile) return null;

    // Random position within tile (center ± 12px), avoiding overlap with existing mines
    const tileCenter = {
      x: col * TILE_SIZE + TILE_SIZE / 2,
      y: row * TILE_SIZE + TILE_SIZE / 2,
    };
    const { x, y } = this.findPlacement(tileCenter.x, tileCenter.y, col, row);

    const mine = new Mine(this.scene, x, y, col, row, stats, evolutionLevel, textureKey);
    mine.setChainCallback((enemy) => this.checkChainReaction(enemy, mine.chainDepth));

    this.mines.push(mine);
    this.tileMineCounts.set(tileKey, currentCount + 1);
    SoundManager.play('mineLay');
    EventBus.emit(GameEvents.MINE_PLACED, { x, y, col, row });
    return mine;
  }

  /** Find a position within a tile that doesn't overlap existing mines too much */
  private findPlacement(cx: number, cy: number, col: number, row: number): { x: number; y: number } {
    const spread = 12;
    const minDist = 10;
    const existingMines = this.mines.filter(
      m => m.alive && m.tileCol === col && m.tileRow === row,
    );

    // Try a few random positions, pick the best one
    let bestX = cx;
    let bestY = cy;
    let bestMinDist = 0;

    for (let attempt = 0; attempt < 8; attempt++) {
      const tx = cx + (Math.random() - 0.5) * spread * 2;
      const ty = cy + (Math.random() - 0.5) * spread * 2;

      let closest = Infinity;
      for (const m of existingMines) {
        const d = Math.hypot(m.x - tx, m.y - ty);
        if (d < closest) closest = d;
      }

      if (closest > bestMinDist) {
        bestMinDist = closest;
        bestX = tx;
        bestY = ty;
        if (closest >= minDist) break; // good enough
      }
    }

    return { x: bestX, y: bestY };
  }

  // ── Update loop ──

  update(delta: number, enemies: Enemy[]): void {
    const dt = delta / 1000;
    this.currentEnemies = enemies;

    // Rebuild spatial hash for proximity queries
    const aliveMines = this.mines.filter(m => m.alive);
    this.mineHash.rebuild(aliveMines, m => m.x, m => m.y);

    // Check enemy proximity → trigger idle mines
    for (const enemy of enemies) {
      if (!enemy.alive) continue;
      const nearby = this.mineHash.queryRadius(enemy.body.x, enemy.body.y, 60);
      for (const mine of nearby) {
        if (mine.state !== MineState.IDLE) continue;
        const dist = Math.hypot(enemy.body.x - mine.x, enemy.body.y - mine.y);
        if (dist <= mine.triggerRadius) {
          mine.trigger();
        }
      }
    }

    // Update all mines (countdown timers)
    for (const mine of this.mines) {
      if (!mine.alive) continue;
      if (mine.state === MineState.TRIGGERED) {
        const wasTriggered = mine.state === MineState.TRIGGERED;
        mine.update(dt);
        // If mine just exploded, apply damage and track kills
        if (wasTriggered && !mine.alive) {
          const kills = mine.applyDamageToEnemies(enemies);
          if (kills > 0) EventBus.emit(GameEvents.MINE_KILLS, kills);
        }
      }
    }

    // Process chain reaction queue
    for (let i = this.chainQueue.length - 1; i >= 0; i--) {
      const entry = this.chainQueue[i];
      entry.delay -= dt;
      if (entry.delay <= 0) {
        if (entry.mine.alive && entry.mine.state === MineState.IDLE) {
          entry.mine.triggerChain(entry.depth);
        }
        this.chainQueue.splice(i, 1);
      }
    }

    // Cleanup dead mines
    for (const mine of this.mines) {
      if (!mine.alive) {
        const key = `${mine.tileCol}_${mine.tileRow}`;
        const count = this.tileMineCounts.get(key) ?? 1;
        if (count <= 1) this.tileMineCounts.delete(key);
        else this.tileMineCounts.set(key, count - 1);
      }
    }
    this.mines = this.mines.filter(m => m.alive);
  }

  // ── Chain reactions ──

  /**
   * After an enemy is pushed back by a mine, check if it landed on another mine.
   * If so, queue that mine for near-instant detonation.
   */
  private checkChainReaction(enemy: Enemy, parentDepth: number): void {
    if (!enemy.alive) return;
    const newDepth = parentDepth + 1;
    if (newDepth > MINE_CONFIG.maxChainDepth) return;

    // Use a small delay before checking — knockBack needs time to move the enemy
    this.scene.time.delayedCall(50, () => {
      if (!enemy.alive) return;
      const nearby = this.mineHash.queryRadius(enemy.body.x, enemy.body.y, 60);
      for (const mine of nearby) {
        if (mine.state !== MineState.IDLE) continue;
        const dist = Math.hypot(enemy.body.x - mine.x, enemy.body.y - mine.y);
        if (dist <= mine.triggerRadius) {
          // Queue chain detonation
          this.chainQueue.push({
            mine,
            delay: MINE_CONFIG.chainDelay,
            depth: newDepth,
          });
        }
      }
    });
  }

  // ── Wave cleanup ──

  clearAll = (): void => {
    for (const mine of this.mines) {
      mine.destroy();
    }
    this.mines = [];
    this.tileMineCounts.clear();
    this.chainQueue = [];
  };

  /** Number of active mines on a tile */
  getMinesOnTile(col: number, row: number): number {
    return this.tileMineCounts.get(`${col}_${row}`) ?? 0;
  }

  /** Max mines allowed on a tile for a given evolution level */
  getMaxForTile(evolutionLevel: number): number {
    return MINE_CONFIG.maxMinesPerTile + evolutionLevel;
  }

  shutdown(): void {
    this.clearAll();
    EventBus.off(GameEvents.WAVE_COMPLETED, this.clearAll, this);
  }
}
